import React, { useEffect, useRef, useState } from "react";
import SuperUserHeader from "../../components/SuperUserHeader";
import SuperUserSideMenu from "../../components/SuperUserSideMenu";
import { useNavigate, useParams } from "react-router-dom";
import TextInput from "../../components/TextInput";
import CustomTextarea from "../../components/CustomTextarea";
import CustomDatePicker from "../../components/CustomDatePicker";
import RadioGroup from "../../components/RadioGroup";
import ConfirmPopup from "../../components/ConfirmPopup";
import CustomButton from "../../components/CustomButton";
import ReturnButton from "../../components/ReturnButton";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import CustomToast from "../../components/CustomToast";
import { LoadingSpinner } from "../../components/Icons";
import { dateToString, formatIdWithLength } from "../../utils/format";
import { validateRequired } from "../../utils/validation"
import {
    setSuccess,
    setError,
    getCustomerCompanyDetail,
    updateCustomerCompanyDetail,
    deleteCustomerCompanyDetail,
} from "../../reducers/customerCompany";
import NotFoundPage from "../../pages/NotFoundPage";
import CustomSwitch from "../../components/CustomSwitch";

const initialData = {
    id: "",
    companyName: "",
    contractStarted: "" as any,
    contractDeadline: "" as any,
    status: true,   
    allowedIP: "",
    description: "",
    logo: null,
    url_slug: "",
    aluminiRegistrationCondition: "",
    otherTalentRegistrationCondition: "",
    privacyPolicy: "",
    internalTalentRegistrationCondition: "",
    isAlumniLinkPublic: false,
    isOtherTalentLinkPublic: false,
    isInternalTalentLinkPublic: false
};

const initialErrorData = {
    companyName: "",
    contractStarted: "",
    contractDeadline: "",
    status: "",
    allowedIP: "",
    description: "",
    logo: "",
    url_slug: "",
    privacyPolicy: ""
};

export const statusOptions = [
    {
      label: "アクティブ",
      value: true,
    },
    {
      label: "非アクティブ",
      value: false,
    },
];

const CompanyEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    const fileRef = useRef(null);
    const [formData, setFormData] = useState(initialData);
    const [errors, setErrors] = useState(initialErrorData);
    const [open, setOpen] = useState(false);
    const [confirmText, setConfirmText] = useState<any>(null);
    const {
        loading,
        success,
        error,
        actionName,
        actionStatus,
        detailData,
    } = useSelector((state: any) => state.customerCompany);
    const { id } = useParams<{ id: string }>();
            
    useEffect(() => {
        if(id){
            const data = {
                id: Number(id),
            }
            dispatch(getCustomerCompanyDetail(data));
        }
    }, [id]);

    useEffect(() => {
        if (detailData.id && String(detailData.id) === String(id)) {
            const data = {
                id: detailData.id,
                companyName: detailData.name,
                contractStarted: dateToString(detailData.contract_started_at),
                contractDeadline: dateToString(detailData.expired_at),
                status: detailData.is_contract_valid,
                allowedIP: detailData.allowed_ip,
                description: detailData.description,
                logo: detailData.logo,
                url_slug: detailData.url_slug,
                aluminiRegistrationCondition: detailData.alumni_registration_condition,
                otherTalentRegistrationCondition: detailData.other_talent_registration_condition,
                privacyPolicy: detailData.privacy_policy,
                internalTalentRegistrationCondition: detailData.internal_talent_registration_condition,
                isAlumniLinkPublic: detailData.is_public_alumni,
                isOtherTalentLinkPublic: detailData.is_public_external_talent,
                isInternalTalentLinkPublic: detailData.is_public_internal_talent
            }
            setFormData(data);
        }
    }, [detailData, id]);

    const validateFormData = () => {
        let formErrors = {
            companyName: "",
            contractStarted: "",
            contractDeadline: "",
            status: "",
            allowedIP: "",
            logo: "",
            url_slug: "",
            privacyPolicy: ""
        };

        const checkCompanyName = validateRequired(formData.companyName, '企業名');
        if (checkCompanyName !== true) {
            formErrors.companyName = checkCompanyName
        }
        const checkContractStarted = validateRequired(formData.contractStarted, '契約開始日');
        if (checkContractStarted !== true) {
            formErrors.contractStarted = checkContractStarted
        }
        const checkContractDeadline = validateRequired(formData.contractDeadline, '契約期限');
        if (checkContractDeadline !== true) {
            formErrors.contractDeadline = checkContractDeadline
        }
        const checkPrivacyPolicy = validateRequired(formData.privacyPolicy, '個人情報保護方針');
        if (checkPrivacyPolicy !== true) {
            formErrors.privacyPolicy = checkPrivacyPolicy
        }

        if(formData.contractStarted && formData.contractDeadline) {
            const contractStartedDate = new Date(dateToString(formData.contractStarted));
            const contractDeadlineDate = new Date(dateToString(formData.contractDeadline));

            if (contractDeadlineDate < contractStartedDate) {
                formErrors.contractDeadline = "契約期限が契約開始日より前です。";
            }
        }

        if(formData.allowedIP) {
            const IPs = formData.allowedIP.split(/[,;|\r?\n]+/);
            if(new Set(IPs).size !== IPs.length) {
                formErrors.allowedIP = "IP アドレスが重複しています。";
            }
        }
        
        setErrors(formErrors);
        return !Object.values(formErrors).some((error) => error);
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        if (validateFormData()) {
            const data = {
                id: Number(id),
                formData: {
                    id: formData.id,
                    name: formData.companyName,
                    contract_started_at: dateToString(formData.contractStarted),
                    expired_at: dateToString(formData.contractDeadline),
                    is_contract_valid: formData.status,
                    allowed_ip: formData.allowedIP,
                    description: formData.description,
                    logo: formData.logo,
                    url_slug: formData.url_slug,
                    alumni_registration_condition: formData.aluminiRegistrationCondition,
                    other_talent_registration_condition: formData.otherTalentRegistrationCondition,
                    privacy_policy: formData.privacyPolicy,
                    internal_talent_registration_condition: formData.internalTalentRegistrationCondition,
                    is_public_alumni: formData.isAlumniLinkPublic,
                    is_public_external_talent: formData.isOtherTalentLinkPublic,
                    is_public_internal_talent: formData.isInternalTalentLinkPublic
                },
            }
            dispatch(updateCustomerCompanyDetail(data));
        }
    };

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleChangeLogo = (e:any) => {
        const file = e.target.files[0];
        if(!file) return;

        // Validate file type
        const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        if (!allowedTypes.includes(file.type)) {
          showToast("メッセージ", "添付ファイルの形式が正しくありません。「.png、.jpg、.jpeg」形式でアップロードしてください。", "error")
          return;
        }

        const maxSize = 300 * 1024; //300KB
        if(file.size > maxSize) {
            showToast('メッセージ', '添付ファイルのサイズが最大値（300KB）を越えています。もう一度ご確認ください。', 'error');
            return;
        }
        setFormData(prev => ({...prev, logo: file}))
    }

    const handleDelete = () => {
        setConfirmText(
            <>
                <p>
                    {`${detailData.name}を削除すると、企業のすべての情報にアクセスができなくなります。本操作は取り消すことができません。`}
                </p>
                <p className="alu-text-base-2 text-sm font-normal mt-[24px]">削除しますか？</p>
            </>
        );
        setOpen(true);
    }

    const handleConfirm = () => {
        dispatch(deleteCustomerCompanyDetail({id: Number(detailData.id)}))
        setOpen(false);
    }

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const showToast = (title: string, message: string, variant: string) => {
        enqueueSnackbar("I love snacks.", {
        content: (key) => (
            <CustomToast title={title} message={message} variant={variant} snackbarKey={key} closeSnackbar={closeSnackbar} />
        ),
        });
    };

    const extractFileName = (fileName: string) => {
        if(!fileName) {
            return ''
        }
        const base = fileName.split('?').slice(0, -1).join('?') || fileName;
        return base.split('/').pop();
    }

    const handleDefaultValueImageName = (fileName: any) => {
        if(!fileName) {
            return ''
        }
        if(fileName?.name) {
            return fileName?.name
        }
        return extractFileName(fileName.split("/").pop())
    }

    useEffect(() => {
        if(error) {
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={error}
                    variant={"error"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setError(null));
        }
        else if(success) {
            if(actionName == "deleteCustomerCompanyDetail") {
                navigate("/control/company");
                return;
            }
            enqueueSnackbar("I love snacks.", {
                content: (key) => (
                <CustomToast
                    title={"メッセージ"}
                    message={success}
                    variant={"success"}
                    snackbarKey={key}
                    closeSnackbar={closeSnackbar}
                />
                ),
            });
            dispatch(setSuccess(null));
        }
    }, [error, success, actionName]);

    if (!detailData || (Array.isArray(detailData) && detailData.length === 0) || Object.keys(detailData).length === 0) {
        if(actionName == "getCustomerCompanyDetail" && actionStatus == "rejected") return <NotFoundPage />;
        return ;
    }

    const handleLogoName = () => {
        
    }

    return (
        <div className="bg-alu-base h-full">
        <SuperUserHeader />
        <div className="mt-6 px-6 flex gap-6 relative pb-8">
            <SuperUserSideMenu />
            <div className="flex-1">
                <div className="flex items-center justify-between">
                    <div className="mb-6 flex items-center gap-4">
                        <ReturnButton onClick={() => navigate("/control/company/detail/" + detailData.id)} />
                        <span className="text-sm alu-text-base-1">/</span>
                        <h3 className="text-xl leading-[1.875rem] alu-text-base-2 font-bold">
                            {detailData.name + " の情報を編集"}
                        </h3>
                    </div>
                    <div className="mb-6">
                        <CustomButton
                            type="button"
                            variant="outline"
                            size="sm"
                            customStyle="!text-[#F33D62] !border-[#F33D62] hover:!bg-[#FFC7F0]"
                            onClick={handleDelete}
                        >
                            この企業を削除
                        </CustomButton>
                    </div>
                </div>

                <div className="bg-white rounded-lg p-4">
                    <form className="w-full">
                        <div className="w-[50%] max-w-[464px]">
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    企業ID
                                </label>
                                <p className="text-sm alu-text-base-2">{formatIdWithLength(formData.id)}</p>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    企業名 <span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full">
                                    <TextInput
                                        name="companyName"
                                        placeholder="入力してください"
                                        value={formData.companyName}
                                        onChange={handleChange}
                                        error={errors.companyName}
                                        errorText={errors.companyName}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    契約開始日 <span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full border-[#F33D62]">
                                    <CustomDatePicker
                                        placeholderText=""
                                        minDate={new Date(new Date().getFullYear() - 1, new Date().getMonth(), new Date().getDate())}
                                        value={formData.contractStarted}
                                        error={errors.contractStarted}
                                        onChange={(newValue: any) =>
                                            setFormData((prev) => ({
                                            ...prev,
                                            contractStarted: newValue,
                                            }))
                                        }
                                    />
                                    {errors.contractStarted && <p className="alu-text-error text-sm mt-2">*{errors.contractStarted}</p>}
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    契約期限 <span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full">
                                    <CustomDatePicker
                                        placeholderText=""
                                        minDate={new Date()}
                                        value={formData.contractDeadline}
                                        error={errors.contractDeadline}
                                        onChange={(newValue: any) =>
                                            setFormData((prev) => ({
                                            ...prev,
                                            contractDeadline: newValue,
                                            }))
                                        }
                                    />
                                    {errors.contractDeadline && <p className="alu-text-error text-sm mt-2">*{errors.contractDeadline}</p>}
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    状態 <span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full">
                                    <RadioGroup
                                        radioWidth={"50%"}
                                        flexWrap={true}
                                        gapSize={"16px"}
                                        colGapSize={"16px"}
                                        name={"status"}
                                        options={statusOptions}
                                        value={formData.status}
                                        onChange={(value: any) =>
                                            setFormData((prev) => ({
                                            ...prev,
                                            status: value,
                                            }))
                                        }
                                        error={errors.status}
                                        errorText={errors.status}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    所属と役職
                                </label>
                                <div className="w-full flex items-center gap-4">
                                    <span 
                                        className="btn-alu-text text-sm px-2 py-[2px] hover:cursor-pointer"
                                        onClick={() => navigate("/control/department/register/" + detailData.id)}
                                    >
                                        所属の登録
                                    </span>
                                    <span className="alu-text-base-2 text-sm">｜</span>
                                    <span 
                                        className="btn-alu-text text-sm px-2 py-[2px] hover:cursor-pointer"
                                        onClick={() => navigate("/control/position/register/" + detailData.id)}
                                    >
                                        役職の登録
                                    </span>
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <label className="block mb-2 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    IP アクセス制限<span className="font-normal">（許可する IPを入力）</span>
                                </label>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        error={errors.allowedIP}
                                        errorText={errors.allowedIP}
                                        name="allowedIP"
                                        placeholder="入力してください"
                                        value={formData.allowedIP}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <label className="block mb-2 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    企業情報
                                </label>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        name="description"
                                        placeholder="入力してください"
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <label className="block mb-2 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    個人情報保護方針<span className="alu-text-error">*</span>
                                </label>
                                <div className="w-full">
                                    <CustomTextarea
                                        maxLength={10000}
                                        rows={4}
                                        name="privacyPolicy"
                                        placeholder="入力してください"
                                        value={formData.privacyPolicy}
                                        onChange={handleChange}
                                    />
                                    {errors.privacyPolicy && <p className="alu-text-error text-sm mt-2">*{errors.privacyPolicy}</p>}
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <label className="block mb-4 whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    企業ロゴ
                                </label>
                                {formData.logo && (
                                    <div className="w-fit h-20 mb-2">
                                        <img
                                        className="w-full h-full object-contain"
                                        src={formData.logo instanceof File ? URL.createObjectURL(formData.logo) : formData.logo}
                                        alt="logo"
                                        />
                                    </div>
                                )}
                                <div className="relative w-full">
                                    <TextInput
                                        error={errors.logo}
                                        errorText={errors.logo}
                                        readOnly
                                        size="md"
                                        name="logo"
                                        onClick={() => fileRef.current.click()}
                                        placeholder="画像ファイルを選択してください"
                                        defaultValue={handleDefaultValueImageName(formData.logo)}
                                        customClassName="hover:cursor-pointer"
                                    />
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileRef}
                                        className="hidden"
                                        onChange={handleChangeLogo}
                                    />
                                    <div className={`absolute h-full ${errors.logo ? "top-[2px] right-[2px]" : "flex items-center top-0 right-[1px]"}`}>
                                        <CustomButton onClick={() => fileRef.current.click()} type="button" size="md"variant="gray" customStyle={`shrink-0 ${errors.logo ? "!h-[2rem]" : "!h-[2.125rem]"} !bg-gray-200 !rounded-l-none !border-t-0 !border-b-0 !border-r-0 !border-l !border-l-[#E0E0E0]`}>参照</CustomButton>
                                    </div>   
                                </div>
                            </div>
                            <div className="grid grid-cols-[124px_1fr] items-center gap-4 mb-4">
                                <label className="flex whitespace-nowrap text-sm font-bold alu-text-base-2">
                                    企業URL
                                </label>
                                <div className="w-full">
                                    <TextInput
                                        name="url_slug"
                                        placeholder="入力してください"
                                        value={formData.url_slug}
                                        onChange={handleChange}
                                        error={errors.url_slug}
                                        errorText={errors.url_slug}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block whitespace-nowrap text-sm font-bold alu-text-base-2">
                                        アルムナイ登録条件
                                    </label>
                                    <div className="flex items-center gap-[0.4375rem]">
                                        <p onClick={() => setFormData(prev => ({...prev, isAlumniLinkPublic: !prev.isAlumniLinkPublic}))} className="text-sm alu-text-base-2 font-bold cursor-pointer">
                                            公開する
                                        </p>
                                        <CustomSwitch
                                            checked={formData.isAlumniLinkPublic}
                                            onChange={() => setFormData(prev => ({...prev, isAlumniLinkPublic: !prev.isAlumniLinkPublic}))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        name="aluminiRegistrationCondition"
                                        placeholder="入力してください"
                                        value={formData.aluminiRegistrationCondition}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block whitespace-nowrap text-sm font-bold alu-text-base-2">
                                        社外タレント登録条件
                                    </label>
                                    <div className="flex items-center gap-[0.4375rem]">
                                        <p onClick={() => setFormData(prev => ({...prev, isOtherTalentLinkPublic: !prev.isOtherTalentLinkPublic}))} className="text-sm alu-text-base-2 font-bold cursor-pointer">
                                            公開する
                                        </p>
                                        <CustomSwitch
                                            checked={formData.isOtherTalentLinkPublic}
                                            onChange={() => setFormData(prev => ({...prev, isOtherTalentLinkPublic: !prev.isOtherTalentLinkPublic}))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        name="otherTalentRegistrationCondition"
                                        placeholder="入力してください"
                                        value={formData.otherTalentRegistrationCondition}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="inline-block w-full mb-4">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="block whitespace-nowrap text-sm font-bold alu-text-base-2">
                                        社内タレント登録条件
                                    </label>
                                    <div className="flex items-center gap-[0.4375rem]">
                                        <p onClick={() => setFormData(prev => ({...prev, isInternalTalentLinkPublic: !prev.isInternalTalentLinkPublic}))} className="text-sm alu-text-base-2 font-bold cursor-pointer">
                                            公開する
                                        </p>
                                        <CustomSwitch
                                            checked={formData.isInternalTalentLinkPublic}
                                            onChange={() => setFormData(prev => ({...prev, isInternalTalentLinkPublic: !prev.isInternalTalentLinkPublic}))}
                                        />
                                    </div>
                                </div>
                                <div className="w-full">
                                    <CustomTextarea
                                        rows={4}
                                        name="internalTalentRegistrationCondition"
                                        placeholder="入力してください"
                                        value={formData.internalTalentRegistrationCondition}
                                        onChange={handleChange}
                                    />
                                </div>
                            </div>
                            <div className="w-full mt-6 grid grid-cols-2 items-center gap-4">
                                <CustomButton
                                    type="button"
                                    size="md"
                                    width="full"
                                    variant="outline"
                                    onClick={() => navigate(-1)}
                                >
                                    戻る
                                </CustomButton>
                                <CustomButton
                                    type="button"
                                    size="md"
                                    width="full"
                                    onClick={handleSubmit}
                                    disabled={loading}
                                >
                                    <p>{loading ? "保存中..." : "更新"}</p>
                                    {loading && (
                                        <span><LoadingSpinner /></span>
                                    )}
                                </CustomButton>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        {open && confirmText && (
            <ConfirmPopup
                type="delete"
                open={open}
                onClose={() => setOpen(false)}
                confirmContent={confirmText}
                onConfirm={handleConfirm}
            />
        )}
        </div>
    );
};

export default CompanyEdit;
